import React, { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { reportsAll, downloadReport } from "@/store/reports";
import { demoReports } from '@/demoData';

import UserReportsTable from "@/components/Tables/UserReportsTable";
import AppModal from "@/components/AppModal";
import ReportConfirmModal from "@/components/Modals/ReportConfirmModal";
import AppPageContainer from "@/components/AppPageContainer";
import AppPagination from "@/components/Tables/AppPagination";
import AgencyReports from "@/pages/client/AgencyReports";

const Reports: React.FC = () => {
  const dispatch = useAppDispatch();

  const { me } = useAppSelector((state) => state.auth);
  const isDemo = me?.id === 207;

  // If agency admin, render new page
  if (me?.is_agency_admin) {
    return <AgencyReports />;
  }

  const { reports, pagination } = useAppSelector((state) => state.reports);
  const tableRows = isDemo ? demoReports : reports;

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isOpen, setIsOpen] = useState(false);
  const [idToDownload, setIdToDownload] = useState<number | null>();
  const [loadingReports, setLoadingReports] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    if (!isDemo && me?.id) {
      // Fetch reports when page or rows per page change
      dispatch(reportsAll({ id: me.id, page: currentPage, perPage: rowsPerPage }));
    }
  }, [dispatch, me, currentPage, rowsPerPage, isDemo]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage + 1); // API uses 1-based index for pages
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to the first page
  };

  const handleDownload = async () => {
    handleToggleModal()
    if (idToDownload) {
      setLoadingReports((prev) => ({ ...prev, [idToDownload]: true }));
      
      try {
        await dispatch(downloadReport(idToDownload)).unwrap();
      } catch (error) {
        console.error("Failed to download report:", error);
      } finally {
        setTimeout(() => {
          setLoadingReports((prev) => ({ ...prev, [idToDownload]: false })); // Re-enable button after delay
        }, 10000); // Reset after 10 seconds
      }

      setIdToDownload(null);
    }
  };

  const handleOpenModal = (selectedId: number) => {
    if (!loadingReports[selectedId]) {
      setIdToDownload(selectedId);
      handleToggleModal();
    }
  }

  const handleToggleModal = () => {
    setIsOpen(!isOpen)
  }

  return (
    <AppPageContainer title="Overall Reports">
      <UserReportsTable tableRows={tableRows} onDownload={handleOpenModal} loadingReports={loadingReports} />

      {
        !isDemo && pagination && (pagination?.total > rowsPerPage)
          ? <AppPagination
              totalRows={pagination?.total || 0}
              rowsPerPage={rowsPerPage}
              currentPage={currentPage - 1} // Convert to 0-based index for the component
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          : null
      }

      <AppModal isOpen={isOpen} title="Confirm Report Download" onClose={handleToggleModal}>
        <ReportConfirmModal onConfirm={handleDownload} onClose={handleToggleModal} />
      </AppModal>
    </AppPageContainer>
  );
};

export default Reports;